// Story 8.10 (ADO-2019): the small display rules the HHT surfaces share: counts in
// words, the /choose card's summary of the waiting orders and the depot mark by depot code. Dates are `shared/datetime.ts`'s (DV-38).

import type { IconName } from '../../design-system';
import { casesLabel } from '../../basket/cartTotals';
import type { HhtWaitingCart, HhtWaitingOrder } from '../../api/hht-orders.client';

/** "1 line" / "N lines". */
export function linesLabel(count: number): string {
  return count === 1 ? '1 line' : `${count} lines`;
}

/** "9 lines · 35 cases" (the 8.10 Figma exports' count format). */
export function linesAndCases(lineCount: number, caseCount: number): string {
  return `${linesLabel(lineCount)} · ${casesLabel(caseCount)}`;
}

/** "+1 substitute" / "+N substitutes". */
export function substitutesLabel(count: number): string {
  return count === 1 ? '+1 substitute' : `+${count} substitutes`;
}

// DV-30, DV-36: Ambient, Chill, Alcohol, the order HHT lists depots in, as the 8.10 Figma
// frames, by their NDC labels (DV-12). From phase 4 a line can also go to the Customer's Default
// VC (DV-53): any other code ranks after them, named by the caller (its /choose tile's label),
// else shown as its code.
const HHT_DEPOT_LABELS: ReadonlyMap<string, string> = new Map([
  ['21', 'Ambient'],
  ['25', 'Chill'],
  ['85', 'Alcohol'],
]);
const DISPLAY_ORDER = [...HHT_DEPOT_LABELS.keys()];
/** Enabler E8-G (ADO-3109): the Chill Fruit & Veg cart's name, as the review names it. */
const FRUIT_AND_VEG_LABEL = 'Fruit & Veg';

/** A depot code without its zero padding ("085" is "85"). */
function normalisedDepotCode(depotCode: string): string {
  return depotCode.trim().replace(/^0+(?=\d)/, '');
}

function displayRank(depotCode: string): number {
  const rank = DISPLAY_ORDER.indexOf(depotCode);
  return rank === -1 ? DISPLAY_ORDER.length : rank;
}

/** Names a depot code outside Ambient, Chill and Alcohol, or null to show the code. */
export type HhtDepotNamer = (depotCode: string) => string | null;

const NO_NAMES: HhtDepotNamer = () => null;

/** DV-36: the carts any waiting order adds to, each once, labelled, in DV-30 order: Ambient,
 *  Chill, Alcohol first, then any other code in code order, and at one depot its own cart
 *  before Fruit & Veg (Chill, then Fruit & Veg). */
function cartsReceiving(waiting: readonly HhtWaitingOrder[], nameOf: HhtDepotNamer): string[] {
  const carts = new Map<string, HhtWaitingCart>();
  for (const cart of waiting.flatMap((order) => order.carts)) {
    const depotCode = normalisedDepotCode(cart.depotCode);
    carts.set(`${cart.entryKind ?? 'DEPOT'}:${depotCode}`, { ...cart, depotCode });
  }
  return [...carts.values()]
    .sort(
      (left, right) =>
        displayRank(left.depotCode) - displayRank(right.depotCode) ||
        left.depotCode.localeCompare(right.depotCode, 'en', { numeric: true }) ||
        Number(left.entryKind !== undefined) - Number(right.entryKind !== undefined),
    )
    .map((cart) =>
      cart.entryKind === 'FRUIT_AND_VEG'
        ? FRUIT_AND_VEG_LABEL
        : (HHT_DEPOT_LABELS.get(cart.depotCode) ?? nameOf(cart.depotCode) ?? cart.depotCode),
    );
}

/**
 * DV-36: the /choose card's second line, a plain sum over the waiting orders (a product in two
 * orders counts twice): "17 lines · 61 cases across Ambient · Chill · Alcohol. 3 lines may need
 * attention after adding to cart." With no line the first sentence is left out (and with no
 * cart to add to, "across" is), with no attention line the second. Null when there is
 * neither.
 */
export function hhtWaitingSummary(
  waiting: readonly HhtWaitingOrder[],
  nameOf: HhtDepotNamer = NO_NAMES,
): string | null {
  const lineCount = waiting.reduce((total, order) => total + order.lineCount, 0);
  const caseCount = waiting.reduce((total, order) => total + order.caseCount, 0);
  const attention = waiting.reduce((total, order) => total + order.attentionLineCount, 0);
  const sentences: string[] = [];
  if (lineCount > 0) {
    const depots = cartsReceiving(waiting, nameOf);
    sentences.push(
      depots.length > 0
        ? `${linesAndCases(lineCount, caseCount)} across ${depots.join(' · ')}.`
        : `${linesAndCases(lineCount, caseCount)}.`,
    );
  }
  if (attention > 0) {
    sentences.push(`${linesLabel(attention)} may need attention after adding to cart.`);
  }
  return sentences.length > 0 ? sentences.join(' ') : null;
}

/** "1 needs attention" / "N need attention" (the waiting orders list, DV-34). */
export function needsAttentionLabel(count: number): string {
  return count === 1 ? '1 needs attention' : `${count} need attention`;
}

export type DepotTone = 'ambient' | 'chill' | 'alcohol' | 'other';

export interface DepotMarkStyle {
  readonly icon: IconName;
  readonly tone: DepotTone;
}

/** The depot mark by NDC code (21 Ambient, 25 Chill, 85 Alcohol), else a storefront. A
 *  code may arrive zero-padded ("021"), so leading zeros are ignored. */
export function depotMarkFor(depotCode: string): DepotMarkStyle {
  switch (normalisedDepotCode(depotCode)) {
    case '21':
      return { icon: 'shoppingBasket', tone: 'ambient' };
    case '25':
      return { icon: 'thermometer', tone: 'chill' };
    case '85':
      return { icon: 'wine', tone: 'alcohol' };
    default:
      return { icon: 'storefront', tone: 'other' };
  }
}
